import { FC, ReactNode } from "react"
import { InspectorPanelPlugin } from "../useInspectorPanel"
import { PluginSpec } from "plugins/plugin-registry"
import { getGenericInspectorPanelHeader } from "./headers/GenericInspectorPanelHeader"
import { getGenericInspectorPanelBody } from "./bodies/GenericInspectorPanelBody"
import { getGenericInspectorPanelFooter } from "./footers/GenericInspectorPanelFooter"
import { zIndex } from "@nova/constants"
import { useProgressDecisions } from "panels/content/side-panel/progress/useProgressDecisions"

export function GenericInspectorPanelContainer({ children }: { children: ReactNode }) {
    return (
        <div
            className="h-fit bg-gray-900 w-[17.5rem] rounded-xl flex flex-col top-2 right-2"
            style={{
                zIndex: zIndex.InspectorPanel,
                maxHeight: "calc(100% - 44px)",
            }}
        >
            {children}
        </div>
    )
}

export function getGenericInspectorPanelComponent({
    spec,
    Header,
    Body,
    Footer,
}: {
    spec: PluginSpec
    Header?: FC<{ id: string; plugin: InspectorPanelPlugin }>
    Body?: FC<{ id: string; plugin: InspectorPanelPlugin }>
    Footer?: FC<{ id: string; plugin: InspectorPanelPlugin }>
}) {
    const GenericInspectorPanel = ({
        id,
        plugin,
    }: {
        id: string | undefined
        plugin: InspectorPanelPlugin
    }) => {
        const { canShowInspectorPanel } = useProgressDecisions({
            id,
            useHook: spec.useUploadStatusAndProgress,
        })
        if (!id || !canShowInspectorPanel) return null
        return (
            <GenericInspectorPanelContainer>
                <div className="flex flex-col min-h-0 shrink">
                    {Header && <Header id={id} plugin={plugin} />}
                    {Body && <Body id={id} plugin={plugin} />}
                    {Footer && <Footer id={id} plugin={plugin} />}
                </div>
            </GenericInspectorPanelContainer>
        )
    }
    return GenericInspectorPanel
}

export function getGenericMockedInspectorPanelComponent({
    Header,
    Body,
    Footer,
}: {
    Header?: FC<{ plugin: InspectorPanelPlugin }>
    Body?: FC<{ plugin: InspectorPanelPlugin }>
    Footer?: FC<{ plugin: InspectorPanelPlugin }>
}) {
    const GenericMockedInspectorPanel = ({ plugin }: { plugin: InspectorPanelPlugin }) => {
        return (
            <GenericInspectorPanelContainer>
                <div className="flex flex-col shrink">
                    {Header && <Header plugin={plugin} />}
                    {Body && <Body plugin={plugin} />}
                    {Footer && <Footer plugin={plugin} />}
                </div>
            </GenericInspectorPanelContainer>
        )
    }
    return GenericMockedInspectorPanel
}

export function getGenericLayerDataInspectorPanelComponent({
    spec,
    LayerComponent,
    DataComponent,
    isScrollable = false,
}: {
    spec: PluginSpec
    LayerComponent: FC<{ id: string }>
    DataComponent: FC<{ id: string }>
    isScrollable?: boolean
}) {
    return getGenericInspectorPanelComponent({
        spec,
        Header: getGenericInspectorPanelHeader(spec),
        Body: getGenericInspectorPanelBody({
            LayerComponent,
            DataComponent,
            isScrollable,
        }),
        Footer: getGenericInspectorPanelFooter(),
    })
}

export function getGenericMockableInspectorPanel({
    Mocked,
    NonMocked,
}: {
    Mocked: FC<{ plugin: InspectorPanelPlugin }>
    NonMocked: FC<{ id: string; plugin: InspectorPanelPlugin }>
}) {
    const GenericMockableInspectorPanel = ({
        id,
        plugin,
    }: {
        id: string | undefined
        plugin: InspectorPanelPlugin
    }) => {
        return !!id ? <NonMocked key={id} id={id} plugin={plugin} /> : <Mocked plugin={plugin} />
    }
    return GenericMockableInspectorPanel
}
